const MAX_ITEMS = 5;

export const PROVINCE_REQUIRED_COLUMNS = [
  'Nombre y Apellido',
  'WhatsApp',
  'Provincia',
  'Distrito',
  'Direccion',
  'Direccion de Agencia',
  'SKU 1',
];

function text(value) {
  return value == null ? '' : String(value).trim();
}

function decodeUtf8Mojibake(value) {
  // Legacy catalogs/templates may contain UTF-8 bytes interpreted as Latin-1 (for example, JunÃ­n).
  return value.replace(/[\u00c2-\u00df][\u0080-\u00bf]/g, sequence => String.fromCharCode(
    ((sequence.charCodeAt(0) & 0x1f) << 6) | (sequence.charCodeAt(1) & 0x3f),
  ));
}

export function cleanProvinceText(value) {
  return decodeUtf8Mojibake(text(value)).normalize('NFC');
}

export function cleanProvinceCatalog(provinces = []) {
  return Array.from(new Set(provinces.map(cleanProvinceText).filter(Boolean)));
}

function normalize(value) {
  return cleanProvinceText(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

function parseAmount(value, field, label, errors) {
  const raw = text(value);
  if (!raw) return '0';
  const number = Number(raw.replace(',', '.'));
  if (!Number.isFinite(number)) {
    errors.push({ field, message: `${label} debe ser numérico` });
    return raw;
  }
  if (number < 0) {
    errors.push({ field, message: `${label} no puede ser negativo` });
  }
  return number.toFixed(2);
}

function collectItems(row, errors, productMap) {
  const items = [];
  for (let index = 1; index <= MAX_ITEMS; index += 1) {
    const sku = text(row[`SKU ${index}`]);
    const rawQuantity = text(row[`Cantidad ${index}`]) || (sku ? '1' : '');
    if (!sku) continue;

    const quantity = Number(rawQuantity);
    if (!Number.isInteger(quantity) || quantity < 1) {
      errors.push({
        field: `Cantidad ${index}`,
        message: `Cantidad ${index} debe ser un entero mayor o igual a 1`,
      });
    }
    if (productMap.size > 0 && !productMap.has(sku.toLowerCase())) {
      errors.push({ field: `SKU ${index}`, message: `SKU no encontrado en tu inventario: ${sku}` });
    }
    items.push({ index, sku, quantity: rawQuantity });
  }
  if (items.length === 0) {
    errors.push({ field: 'SKU 1', message: 'Debe proporcionar al menos un SKU' });
  }
  return items;
}

export function validateProvinceDelivery(row, rowNumber, provinces = [], userProducts = []) {
  const errors = [];
  const warnings = [];
  const provinceMap = new Map(cleanProvinceCatalog(provinces).map(province => [normalize(province), province]));
  const productMap = new Map(userProducts.map(product => [text(product.sku).toLowerCase(), product]));

  const name = text(row['Nombre y Apellido']);
  const phone = text(row.WhatsApp).replace(/\D/g, '');
  const dni = text(row.DNI);
  const provinceRaw = cleanProvinceText(row.Provincia);
  const province = provinceMap.get(normalize(provinceRaw)) || provinceRaw;
  const district = text(row.Distrito);
  const address = text(row.Direccion);
  const agencyAddress = text(row['Direccion de Agencia']);
  const notes = text(row.Notas);

  if (!name) errors.push({ field: 'Nombre y Apellido', message: 'Nombre y Apellido es obligatorio' });
  else if (name.length < 3) warnings.push({ field: 'Nombre y Apellido', message: 'El nombre parece muy corto' });

  if (phone.length !== 9) errors.push({ field: 'WhatsApp', message: 'WhatsApp debe tener exactamente 9 dígitos' });
  else if (!phone.startsWith('9')) warnings.push({ field: 'WhatsApp', message: 'WhatsApp normalmente empieza con 9 en Perú' });

  if (dni && !/^\d{8}$/.test(dni)) errors.push({ field: 'DNI', message: 'DNI debe tener exactamente 8 dígitos' });
  if (!provinceRaw || !provinceMap.has(normalize(provinceRaw))) {
    errors.push({ field: 'Provincia', message: `Provincia "${provinceRaw}" no reconocida` });
  }
  if (!district) errors.push({ field: 'Distrito', message: 'Distrito es obligatorio' });
  if (!address) errors.push({ field: 'Direccion', message: 'Dirección es obligatoria' });
  else if (address.length < 8) warnings.push({ field: 'Direccion', message: 'La dirección parece muy corta' });
  if (!agencyAddress) errors.push({ field: 'Direccion de Agencia', message: 'Dirección de Agencia es obligatoria' });

  const charge = parseAmount(row.Cobrar, 'Cobrar', 'Cobrar', errors);
  const depositAdvance = parseAmount(row.Adelanto, 'Adelanto', 'Adelanto', errors);
  const items = collectItems(row, errors, productMap);

  items.forEach((item) => {
    const product = productMap.get(item.sku.toLowerCase());
    const quantity = Number(item.quantity);
    if (product && Number.isInteger(quantity) && quantity > Number(product.available_quantity ?? 0)) {
      warnings.push({
        field: `SKU ${item.index}`,
        message: `${item.sku}: cantidad ${quantity} supera el stock visible (${product.available_quantity ?? 0})`,
      });
    }
  });

  const data = {
    'Nombre y Apellido': name,
    WhatsApp: phone,
    DNI: dni,
    Provincia: province,
    Distrito: district,
    Direccion: address,
    'Direccion de Agencia': agencyAddress,
    Cobrar: charge,
    Adelanto: depositAdvance,
    Notas: notes,
  };
  for (let index = 1; index <= MAX_ITEMS; index += 1) {
    const item = items.find(candidate => candidate.index === index);
    data[`SKU ${index}`] = item?.sku || '';
    data[`Cantidad ${index}`] = item?.quantity || (index === 1 ? '1' : '');
  }

  return {
    rowNumber,
    data,
    errors,
    warnings,
    isValid: errors.length === 0,
    hasWarnings: warnings.length > 0,
  };
}

export function validateAllProvinceDeliveries(rows, provinces, userProducts) {
  return rows.map((row, index) => validateProvinceDelivery(row, index + 2, provinces, userProducts));
}

export function provinceDeliveryToApi(delivery) {
  const { data, rowNumber } = delivery;
  const items = [];
  for (let index = 1; index <= MAX_ITEMS; index += 1) {
    const sku = text(data[`SKU ${index}`]);
    if (!sku) continue;
    items.push({
      sku,
      quantity: Number(text(data[`Cantidad ${index}`]) || '1'),
    });
  }

  return {
    row_number: rowNumber,
    recipient_name: data['Nombre y Apellido'],
    recipient_phone: data.WhatsApp,
    recipient_dni: data.DNI || '',
    province: data.Provincia,
    district: data.Distrito,
    address: data.Direccion,
    agency_address: data['Direccion de Agencia'],
    charge: data.Cobrar || 0,
    deposit_advance: data.Adelanto || 0,
    notes: data.Notas || '',
    items,
  };
}
